/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/callbackRulesApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API functions for Callback Rules CRUD operations.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  CallbackRule,
  CallbackRuleListQuery,
  CallbackRuleListResponse,
  CallbackRuleDetailResponse,
  CallbackRuleCreatePayload,
  CallbackRuleUpdatePayload
} from '../types';

export async function fetchCallbackRules(query: CallbackRuleListQuery): Promise<CallbackRuleListResponse> {
  const queryParams = new URLSearchParams();
  if (query.page) queryParams.append('page', query.page.toString());
  if (query.limit) queryParams.append('limit', query.limit.toString());
  if (query.sortBy) queryParams.append('sortBy', query.sortBy);
  if (query.sortOrder) queryParams.append('sortOrder', query.sortOrder);
  if (query.search) queryParams.append('search', query.search);
  if (query.domainId) queryParams.append('domainId', query.domainId.toString());

  const response = await fetch(buildApiUrl(`/api/telephony/callback-rules?${queryParams}`), {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch callback rules' }));
    throw new Error(error.message || 'Failed to fetch callback rules');
  }

  return response.json();
}

export async function fetchCallbackRuleById(id: string | number): Promise<CallbackRule> {
  const response = await fetch(buildApiUrl(`/api/telephony/callback-rules/${id}`), {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch callback rule' }));
    throw new Error(error.message || 'Failed to fetch callback rule');
  }

  const result: CallbackRuleDetailResponse = await response.json();
  return result.data;
}

export async function createCallbackRule(payload: CallbackRuleCreatePayload): Promise<CallbackRule> {
  const response = await fetch(buildApiUrl('/api/telephony/callback-rules'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create callback rule' }));
    throw new Error(error.message || 'Failed to create callback rule');
  }

  const result: CallbackRuleDetailResponse = await response.json();
  return result.data;
}

export async function updateCallbackRule(id: string | number, payload: CallbackRuleUpdatePayload): Promise<CallbackRule> {
  const response = await fetch(buildApiUrl(`/api/telephony/callback-rules/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update callback rule' }));
    throw new Error(error.message || 'Failed to update callback rule');
  }

  const result: CallbackRuleDetailResponse = await response.json();
  return result.data;
}

export async function deleteCallbackRule(id: string | number): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/callback-rules/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete callback rule' }));
    throw new Error(error.message || 'Failed to delete callback rule');
  }
}
